import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mic2, MicOff, CheckCircle, Lock, Crown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { FREE_RECORDING_LIMIT } from '../audio/constants';

// Three stacked banners that summarize the microphone permission /
// FREE-tier state. Lifted from RecorderPage verbatim so the JSX
// behaviour is unchanged: only renders the banner that matches the
// current state, mounts via framer-motion entry animation.

const MicStatusCard = ({
  micPermission,
  micError,
  isRecording,
  audioUrl,
  isAtFreeLimit,
  onRequestAccess,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {micPermission !== 'granted' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-4 mb-6 ${
            micPermission === 'denied' || micPermission === 'error'
              ? 'bg-red-500/10 border border-red-500/30'
              : 'bg-blue/10 border border-blue/30'
          }`}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {micPermission === 'denied' || micPermission === 'error' ? (
                <MicOff className="w-5 h-5 text-red-400" />
              ) : (
                <Mic2 className="w-5 h-5 text-blue" />
              )}
              <span className="text-white">
                {micError || t('recorder.startPrompt')}
              </span>
            </div>
            {micPermission !== 'denied' && (
              <Button
                onClick={onRequestAccess}
                disabled={micPermission === 'requesting'}
                className="bg-blue hover:bg-blue/80 text-white"
                data-testid="enable-mic-btn"
              >
                {micPermission === 'requesting' ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Mic2 className="w-4 h-4 mr-2" />
                )}
                Abilita Microfono
              </Button>
            )}
          </div>
        </motion.div>
      )}

      {micPermission === 'granted' && !isRecording && !audioUrl && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-400 font-medium">Microfono attivo - Pronto per registrare</span>
          </div>
        </motion.div>
      )}

      {isAtFreeLimit && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber/10 border border-amber/30 rounded-2xl p-4 mb-6 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber" />
            <span className="text-white">
              Limite Free raggiunto ({FREE_RECORDING_LIMIT} registrazioni). Passa a PRO per registrazioni illimitate!
            </span>
          </div>
          <Link to="/premium">
            <Button size="sm" className="bg-amber hover:bg-amber/80 text-black font-semibold">
              <Crown className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
          </Link>
        </motion.div>
      )}
    </>
  );
};

export default MicStatusCard;
